import { useAuth } from "hooks/Auth";
import React, { useCallback, useEffect, useState } from "react";
import { apiGet } from "helpers/Request";
import { transform as contentTransformer } from "schemas/Content";
import { Link } from "react-router-dom";
import { fromNow, fromSqlDateTime } from "helpers/Date";

const searchProps = {
    author: "",
    tag: "",
    edited_by: "",
    keyword: "",
    published_after: "",
    published_before: "",
    created_after: "",
    created_before: "",
    image_keyword: "",
    image_metadata_label: "",
    image_metadata_value: "",
    content_type: "",
    workflow: "",
    nonce: -1,
};

function diffDate(time) {
    const now = fromNow();
    const end = fromSqlDateTime(time);
    const diff = now
        .diff(end, ["minutes", "hours", "days", "months", "years"])
        .toObject();

    if (diff.years > 0) {
        return `${diff.years} years`;
    } else if (diff.months > 0) {
        return `${diff.months} months`;
    } else if (diff.days > 0) {
        return `${diff.days} days`;
    } else if (diff.hours > 0) {
        return `${diff.hours} hours`;
    } else if (diff.minutes > 0) {
        return `${diff.minutes} minutes`;
    }

    return "fresh as a daisy";
}

export default function Dam(props) {
    const uuid = props.item?.uuid || null;
    const [item, setItem] = useState(null);

    const auth = useAuth();

    const generateLink = (vals) => {
        const base = "/app/search";
        const params = Object.assign({}, searchProps, vals);
        return base + "?" + new URLSearchParams(params).toString();
    };

    const loadData = useCallback(() => {
        if (!uuid) {
            return;
        }
        apiGet(`/v4/content/${uuid}`, auth.publication, {
            include: "related,siblings,keywords,imageKeywords,imageMetadata",
        }).then((res) => {
            setItem(contentTransformer(res));
        });
    }, [auth.publication, uuid]);

    useEffect(() => {
        loadData();
    }, [loadData, uuid]);

    if (!uuid) {
        return null;
    }

    if (!item) {
        return null;
    }

    // Collections Batch Siblings ML Keywords Rake Keywords Age

    return (
        <div className="space-y-6">
            <div className="gap-4 gap-y-8 space-y-4 md:grid md:grid-cols-2 md:space-y-0">
                <div className="space-y-2">
                    <h3 className="text-1xl font-bold">Extended Info</h3>
                    <div className="max-h-56 space-y-2 overflow-scroll rounded bg-white p-4 text-xs">
                        <div>Type: {item.type}</div>
                        <div>
                            Extended Type: {item.attachment?.type || "none"}
                        </div>
                        <div>Status: {item.status}</div>
                        <div>Age: {diffDate(item.created_at)}</div>
                    </div>
                    <p className="text-xs"></p>
                </div>

                <div className="space-y-2">
                    <h3 className="text-1xl font-bold">Text Keywords</h3>
                    <div className="max-h-56 space-y-2 overflow-scroll rounded bg-white p-4 text-xs">
                        {item.keywords.length ? (
                            item.keywords?.map((val, i) => {
                                return <div key={`val-${i}`}>{val}</div>;
                            })
                        ) : (
                            <div className="text-gray-400">
                                No keywords found
                            </div>
                        )}
                    </div>
                    <p className="text-xs">
                        CEO extracts common keywords and phrases from your text
                        in order to relate content.
                    </p>
                </div>

                <div className="space-y-2">
                    <h3 className="text-1xl font-bold">Related</h3>
                    <div className="max-h-56 space-y-2 overflow-scroll rounded bg-white p-4 text-xs">
                        {item.related.length ? (
                            item.related?.map((val, i) => {
                                return (
                                    <div key={`rval-${i}`}>
                                        <Link
                                            className="text-green-600 underline"
                                            to={`/app/${val.type}/${val.uuid}`}
                                        >
                                            {val.title || val.uuid}
                                        </Link>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-gray-400">
                                No related items found
                            </div>
                        )}
                    </div>
                    <p className="text-xs">
                        Items CEO matches this content with, via keywords.
                    </p>
                </div>

                <div className="space-y-2">
                    <h3 className="text-1xl font-bold">Siblings</h3>
                    <div className="max-h-56 space-y-2 overflow-scroll rounded bg-white p-4 text-xs">
                        {item.siblings.length ? (
                            item.siblings?.map((val, i) => {
                                return (
                                    <div key={`sval-${i}`}>
                                        <Link
                                            className="text-green-600 underline"
                                            to={`/app/${val.type}/${val.uuid}`}
                                        >
                                            {val.title || val.uuid}
                                        </Link>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-gray-400">
                                No siblings found
                            </div>
                        )}
                    </div>
                    <p className="text-xs">
                        CEO tracks items that were uploaded at the same time.
                    </p>
                </div>

                <div className="space-y-2">
                    <h3 className="text-1xl font-bold">Image Metadata</h3>
                    <div className="max-h-56 space-y-2 overflow-scroll rounded bg-white p-4 text-xs">
                        {item.imageMetadata.length ? (
                            item.imageMetadata?.map((val, i) => {
                                return (
                                    <div key={`imval-${i}`}>
                                        {val.label}:{" "}
                                        <Link
                                            className="text-green-600 underline"
                                            to={generateLink({
                                                image_metadata_label: val.label,
                                                image_metadata_value: val.value,
                                            })}
                                        >
                                            {val.value}
                                        </Link>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-gray-400">
                                No metadata found
                            </div>
                        )}
                    </div>
                    <p className="text-xs">
                        Image metadata is extracted from IPTC and EXIF keyword
                        data embedded in the file itself.
                    </p>
                </div>

                <div className="space-y-2">
                    <h3 className="text-1xl font-bold">Image Keywords</h3>
                    <div className="max-h-56 space-y-2 overflow-scroll rounded bg-white p-4 text-xs">
                        {item.imageKeywords.length ? (
                            item.imageKeywords?.map((val, i) => {
                                return (
                                    <div key={`ikval-${i}`}>
                                        <Link
                                            className="text-green-600 underline"
                                            to={generateLink({
                                                image_keyword: val.keyword,
                                            })}
                                        >
                                            {val.keyword}
                                        </Link>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-gray-400">
                                No keywords found
                            </div>
                        )}
                    </div>
                    <p className="text-xs">
                        Images are processed through a machine learning
                        algorithm that attempts to determine the content of the
                        image.
                    </p>
                </div>

                <div className="space-y-2">
                    <h3 className="text-1xl font-bold">Collections</h3>
                    <div className="max-h-56 space-y-2 overflow-scroll rounded bg-white p-4 text-xs">
                        {item.collections.length ? (
                            item.collections?.map((val, i) => {
                                return (
                                    <div key={`cval-${i}`}>
                                        <Link
                                            className="text-green-600 underline"
                                            to={`/app/dashboard?collection=${val.id}`}
                                        >
                                            {val.name}
                                        </Link>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-gray-400">
                                No collections found
                            </div>
                        )}
                    </div>
                    <p className="text-xs">
                        You can group content into collections via the search
                        interface.
                    </p>
                </div>
            </div>
        </div>
    );
}
